/**
 * ActionButton — Save to watchlist / Coach contact button.
 * Matches detail-atoms.jsx ActionButton.
 *
 * primary  = true  → brand-primary green pill
 * locked   = true  → subdued icon opacity (legacy prop, kept for callers that use it)
 * disabled = true  → full-button reduced opacity, aria-disabled, pointer-events-none.
 *                    Uses aria-disabled (not native disabled) so the button stays in
 *                    the tab order and screen readers can announce its presence and label.
 */
import React from 'react';

interface Props {
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  sub?: string;
  primary?: boolean;
  locked?: boolean;
  disabled?: boolean;
  full?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
}

export function ActionButton({
  icon: Icon,
  label,
  sub,
  primary = false,
  locked = false,
  disabled = false,
  full = false,
  onClick,
  ariaLabel,
}: Props) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      aria-label={ariaLabel ?? label}
      aria-disabled={disabled ? 'true' : undefined}
      className={[
        'flex items-center gap-2.5 px-3 py-2.5 min-h-[44px] rounded-lg border',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-1',
        'motion-safe:transition-colors text-left',
        full ? 'w-full' : '',
        disabled ? 'opacity-50 pointer-events-none' : '',
        primary
          ? 'bg-brand-primary border-brand-primary text-white hover:bg-brand-primary-dk'
          : 'bg-surface-card border-hairline text-gray-900 hover:border-brand-primary',
      ].join(' ')}
    >
      <span className={`flex-shrink-0 ${locked ? 'opacity-60' : ''}`}>
        <Icon size={16} />
      </span>
      <span className="flex flex-col min-w-0">
        <span className="text-[13px] font-semibold leading-tight">{label}</span>
        {sub && (
          <span
            className={`text-[10.5px] mt-0.5 ${primary ? 'text-green-200' : 'text-gray-500'}`}
          >
            {sub}
          </span>
        )}
      </span>
    </button>
  );
}
